import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { Calculator, Send, ArrowLeft, DollarSign, Clock, Wrench, TrendingUp, Settings2, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function ROICalculator() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fleet_manager_salary: 65000,
    hours_per_repair_inhouse: 2.5,
    vendor_markup_savings_percent: 12,
    downtime_cost_per_hour: 85,
    admin_overhead_percent: 20,
  });

  useEffect(() => {
    loadROI();
  }, [customerId]);

  const loadROI = async () => {
    try {
      const res = await api.get(`/analytics/roi/${customerId}`);
      setData(res.data);
      setSettings(res.data.settings);
    } catch (e) {
      toast.error('Failed to load ROI data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put(`/analytics/roi/${customerId}/settings`, settings);
      toast.success('Settings saved');
      setSettingsOpen(false);
      loadROI();
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const sendToCustomer = async () => {
    setSending(true);
    try {
      await api.post(`/analytics/roi/${customerId}/share`);
      toast.success('ROI report sent to customer');
    } catch (e) {
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="text-[#3B82F6] animate-spin" size={28} />
      </div>
    );
  }

  const { metrics = {}, savings = {}, roi = {}, comparison = {} } = data || {};
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="roi-calculator-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// ROI CALCULATOR</p>
            <h1 className="text-white font-['Manrope'] font-extrabold text-2xl tracking-tight">{data?.company_name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="border-[#2D3F5E] text-[#8B9AAB] hover:border-[#3B82F6] hover:text-[#3B82F6]">
                <Settings2 size={14} className="mr-2" />
                Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-[#2D3F5E]">
              <DialogHeader>
                <DialogTitle className="text-[#E8EAED] font-['Manrope']">ROI Calculation Settings</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-[#8B9AAB]">Fleet Manager Salary (Annual)</Label>
                  <Input
                    type="number"
                    value={settings.fleet_manager_salary}
                    onChange={(e) => setSettings({ ...settings, fleet_manager_salary: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1E293B] border-[#2D3F5E] text-[#E8EAED]"
                  />
                </div>
                <div>
                  <Label className="text-[#8B9AAB]">Hours per Repair (In-house)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={settings.hours_per_repair_inhouse}
                    onChange={(e) => setSettings({ ...settings, hours_per_repair_inhouse: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1E293B] border-[#2D3F5E] text-[#E8EAED]"
                  />
                </div>
                <div>
                  <Label className="text-[#8B9AAB]">Vendor Markup Savings (%)</Label>
                  <Input
                    type="number"
                    value={settings.vendor_markup_savings_percent}
                    onChange={(e) => setSettings({ ...settings, vendor_markup_savings_percent: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1E293B] border-[#2D3F5E] text-[#E8EAED]"
                  />
                </div>
                <div>
                  <Label className="text-[#8B9AAB]">Downtime Cost ($/hour)</Label>
                  <Input
                    type="number"
                    value={settings.downtime_cost_per_hour}
                    onChange={(e) => setSettings({ ...settings, downtime_cost_per_hour: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1E293B] border-[#2D3F5E] text-[#E8EAED]"
                  />
                </div>
                <div>
                  <Label className="text-[#8B9AAB]">Admin Overhead (%)</Label>
                  <Input
                    type="number"
                    value={settings.admin_overhead_percent}
                    onChange={(e) => setSettings({ ...settings, admin_overhead_percent: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1E293B] border-[#2D3F5E] text-[#E8EAED]"
                  />
                </div>
                <Button onClick={saveSettings} className="w-full bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90">
                  Save Settings
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={sendToCustomer} disabled={sending} className="bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90">
            {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
            Send to Customer
          </Button>
        </div>
      </div>

      {/* Hero - Total Savings */}
      <div className="text-center py-8 bg-gradient-to-b from-[#57EB31]/5 to-transparent border border-[#57EB31]/20 rounded-lg">
        <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#5C6A7A] mb-2">ESTIMATED ANNUAL SAVINGS</p>
        <p className="text-6xl font-['JetBrains_Mono'] font-bold text-[#57EB31]" data-testid="total-savings">
          {fmt(savings.total_savings)}
        </p>
        <p className="text-[#8B9AAB] font-['IBM_Plex_Sans'] text-sm mt-2">
          Based on {metrics.total_repairs} repairs managed
        </p>
      </div>

      {/* Savings Breakdown */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-industrial p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-[#3B82F6]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">LABOR SAVINGS</span>
          </div>
          <p className="text-2xl font-['JetBrains_Mono'] font-bold text-[#3B82F6]">{fmt(savings.labor_savings)}</p>
          <p className="text-xs text-[#8B9AAB] mt-1">{savings.hours_saved?.toFixed(0)} hours saved</p>
        </div>
        <div className="card-industrial p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={16} className="text-[#57EB31]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">NEGOTIATION SAVINGS</span>
          </div>
          <p className="text-2xl font-['JetBrains_Mono'] font-bold text-[#57EB31]">{fmt(savings.negotiation_savings)}</p>
          <p className="text-xs text-[#8B9AAB] mt-1">{settings.vendor_markup_savings_percent}% vendor markup avoided</p>
        </div>
        <div className="card-industrial p-5">
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={16} className="text-teal-400" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">DOWNTIME SAVINGS</span>
          </div>
          <p className="text-2xl font-['JetBrains_Mono'] font-bold text-teal-400">{fmt(savings.downtime_savings)}</p>
          <p className="text-xs text-[#8B9AAB] mt-1">15% faster turnaround</p>
        </div>
      </div>

      {/* ROI Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card-industrial p-6">
          <h3 className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-4">// RETURN ON INVESTMENT</h3>
          <div className="flex items-end gap-4">
            <div>
              <p className="text-5xl font-['JetBrains_Mono'] font-bold text-[#3B82F6]" data-testid="roi-percent">
                {roi.roi_percent?.toFixed(0)}%
              </p>
              <p className="text-[#8B9AAB] text-sm mt-1">ROI</p>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-[#5C6A7A]">Service Cost</span>
                <span className="text-[#8B9AAB] font-['JetBrains_Mono']">{fmt(roi.estimated_service_cost)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#5C6A7A]">Total Savings</span>
                <span className="text-[#57EB31] font-['JetBrains_Mono']">{fmt(savings.total_savings)}</span>
              </div>
              <div className="flex justify-between text-xs border-t border-[#2D3F5E] pt-2">
                <span className="text-[#8B9AAB] font-bold">Net Benefit</span>
                <span className="text-[#57EB31] font-['JetBrains_Mono'] font-bold">{fmt(roi.net_benefit)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card-industrial p-6">
          <h3 className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-4">// COST COMPARISON</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-xs text-[#8B9AAB]">Self-Managed (Estimated)</span>
                <span className="text-sm font-['JetBrains_Mono'] text-red-400">{fmt(comparison.self_managed_cost)}</span>
              </div>
              <div className="h-2 bg-[#1E293B] rounded overflow-hidden">
                <div className="h-full bg-red-500/50" style={{ width: '100%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-xs text-[#8B9AAB]">With Our Service</span>
                <span className="text-sm font-['JetBrains_Mono'] text-[#57EB31]">{fmt(comparison.with_service_cost)}</span>
              </div>
              <div className="h-2 bg-[#1E293B] rounded overflow-hidden">
                <div 
                  className="h-full bg-[#57EB31]/50" 
                  style={{ width: `${(comparison.with_service_cost / comparison.self_managed_cost * 100) || 50}%` }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Repairs', value: metrics.total_repairs || 0, color: 'text-[#3B82F6]' },
          { label: 'Completed', value: metrics.completed_repairs || 0, color: 'text-[#57EB31]' },
          { label: 'Total Spend', value: fmt(metrics.total_spend), color: 'text-[#8B9AAB]', mono: true },
          { label: 'Downtime Hours', value: metrics.total_downtime_hours?.toFixed(0) || 0, color: 'text-orange-400' },
        ].map(({ label, value, color, mono }) => (
          <div key={label} className="card-industrial p-4">
            <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">{label}</span>
            <p className={`text-xl font-bold mt-2 ${color} ${mono ? "font-['JetBrains_Mono']" : "font-['Manrope']"}`}>{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
